/**
 * DocumentRequirementsTab — Settings → Document Requirements
 *
 * Two sub-tabs:
 *   Types  — CRUD for document_requirement_types (catalog)
 *   Formula — grid of document_requirement_rules + CSV import/export
 *
 * Shown only when feature flag `document_requirements_engine` is ON.
 * Rendered by SettingsPage when tab value === 'document-requirements'.
 */
import { useState, useRef } from 'react';
import {
  Plus,
  Pencil,
  Trash2,
  Check,
  X,
  Upload,
  Download,
  ClipboardList,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { ViewTabs } from '@/components/ui/view-tabs';
import { cn } from '@/lib/utils';
import {
  useDocumentRequirementTypes,
  useCreateRequirementType,
  useUpdateRequirementType,
  useDeleteRequirementType,
  useDocumentRequirementRules,
  useCreateRequirementRule,
  useUpdateRequirementRule,
  useDeleteRequirementRule,
  useImportRequirementRules,
  type DocumentRequirementType,
  type DocumentRequirementRule,
} from '@/hooks/useDocumentRequirements';

// ============================================================================
// Constants
// ============================================================================

const RECIPIENT_ROLES = [
  { value: 'primary_client', label: 'Primary Client' },
  { value: 'landlord', label: 'Landlord' },
  { value: 'general_contractor', label: 'General Contractor' },
  { value: 'subcontractor', label: 'Subcontractor' },
  { value: 'consultant', label: 'Consultant' },
  { value: 'internal_staff', label: 'Internal Staff' },
  { value: 'custom_email', label: 'Custom Email' },
] as const;

const SUB_TABS = [
  { value: 'types', label: 'Types' },
  { value: 'formula', label: 'Formula' },
];

// ============================================================================
// Types Tab
// ============================================================================

interface TypeRowProps {
  type: DocumentRequirementType;
  onEdit: () => void;
  onDelete: () => void;
}

function TypeRow({ type, onEdit, onDelete }: TypeRowProps) {
  return (
    <tr className="border-t hover:bg-muted/20 group">
      <td className="px-3 py-2 font-mono text-xs text-muted-foreground">{type.code}</td>
      <td className="px-3 py-2 text-sm font-medium">{type.display_name ?? '—'}</td>
      <td className="px-3 py-2 text-xs text-muted-foreground truncate max-w-[200px]">
        {type.description ?? '—'}
      </td>
      <td className="px-3 py-2 text-xs text-muted-foreground">
        {type.default_recipient_role ?? '—'}
      </td>
      <td className="px-3 py-2">
        <span
          className={cn(
            'inline-flex rounded-full px-2 py-0.5 text-[11px] font-medium border',
            type.active
              ? 'bg-green-50 text-green-700 border-green-200'
              : 'bg-gray-50 text-gray-500 border-gray-200',
          )}
        >
          {type.active ? 'Active' : 'Inactive'}
        </span>
      </td>
      <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100">
          <button
            className="p-1 hover:bg-muted rounded text-muted-foreground"
            title="Edit"
            onClick={onEdit}
          >
            <Pencil className="w-3.5 h-3.5" />
          </button>
          <button
            className="p-1 hover:bg-destructive/10 hover:text-destructive rounded text-muted-foreground"
            title="Deactivate"
            onClick={onDelete}
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      </td>
    </tr>
  );
}

interface EditTypeRowProps {
  type?: DocumentRequirementType;
  onSave: (data: any) => void;
  onCancel: () => void;
  isPending: boolean;
}

function EditTypeRow({ type, onSave, onCancel, isPending }: EditTypeRowProps) {
  const [code, setCode] = useState(type?.code ?? '');
  const [displayName, setDisplayName] = useState(type?.display_name ?? '');
  const [description, setDescription] = useState(type?.description ?? '');
  const [recipientRole, setRecipientRole] = useState(type?.default_recipient_role ?? '__none__');

  const handleSave = () => {
    if (!code.trim()) return;
    onSave({
      code: code.trim(),
      display_name: displayName.trim() || undefined,
      description: description.trim() || undefined,
      default_recipient_role: recipientRole && recipientRole !== '__none__' ? recipientRole : undefined,
    });
  };

  return (
    <tr className="border-t bg-blue-50/40">
      <td className="px-3 py-2">
        <Input
          placeholder="CODE"
          value={code}
          onChange={(e) => setCode(e.target.value)}
          className="h-7 text-xs font-mono"
          disabled={!!type}
          autoFocus={!type}
        />
      </td>
      <td className="px-3 py-2">
        <Input
          placeholder="Display name"
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
          className="h-7 text-xs"
          autoFocus={!!type}
        />
      </td>
      <td className="px-3 py-2">
        <Input
          placeholder="Description (optional)"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          className="h-7 text-xs"
        />
      </td>
      <td className="px-3 py-2">
        <Select value={recipientRole} onValueChange={setRecipientRole}>
          <SelectTrigger className="h-7 text-xs">
            <SelectValue placeholder="Recipient role" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__none__">— None —</SelectItem>
            {RECIPIENT_ROLES.map((r) => (
              <SelectItem key={r.value} value={r.value}>
                {r.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </td>
      <td className="px-3 py-2" />
      <td className="px-3 py-2">
        <div className="flex items-center gap-1">
          <button
            className="p-1 hover:bg-green-100 rounded text-green-600"
            title="Save"
            onClick={handleSave}
            disabled={isPending || !code.trim()}
          >
            <Check className="w-3.5 h-3.5" />
          </button>
          <button
            className="p-1 hover:bg-muted rounded text-muted-foreground"
            title="Cancel"
            onClick={onCancel}
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      </td>
    </tr>
  );
}

function TypesTab() {
  const { data: types = [], isLoading } = useDocumentRequirementTypes();
  const create = useCreateRequirementType();
  const update = useUpdateRequirementType();
  const del = useDeleteRequirementType();

  const [addingNew, setAddingNew] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<DocumentRequirementType | null>(null);

  if (isLoading) {
    return <p className="text-sm text-muted-foreground">Loading…</p>;
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold">Requirement Types</h3>
          <p className="text-xs text-muted-foreground mt-0.5">
            The catalog of document types your formula can reference.
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          className="h-8"
          onClick={() => setAddingNew(true)}
          disabled={addingNew}
        >
          <Plus className="w-3.5 h-3.5 mr-1" />
          Add Type
        </Button>
      </div>

      <div className="border rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-muted/30 text-xs text-muted-foreground">
              <th className="px-3 py-2 text-left w-32">Code</th>
              <th className="px-3 py-2 text-left">Name</th>
              <th className="px-3 py-2 text-left">Description</th>
              <th className="px-3 py-2 text-left w-36">Default Recipient</th>
              <th className="px-3 py-2 text-left w-20">Status</th>
              <th className="px-3 py-2 w-16" />
            </tr>
          </thead>
          <tbody>
            {types.map((type) =>
              editingId === type.id ? (
                <EditTypeRow
                  key={type.id}
                  type={type}
                  onSave={(data) => {
                    update.mutate({ id: type.id, ...data });
                    setEditingId(null);
                  }}
                  onCancel={() => setEditingId(null)}
                  isPending={update.isPending}
                />
              ) : (
                <TypeRow
                  key={type.id}
                  type={type}
                  onEdit={() => setEditingId(type.id)}
                  onDelete={() => setDeleteTarget(type)}
                />
              ),
            )}
            {addingNew && (
              <EditTypeRow
                onSave={(data) => {
                  create.mutate(data);
                  setAddingNew(false);
                }}
                onCancel={() => setAddingNew(false)}
                isPending={create.isPending}
              />
            )}
            {types.length === 0 && !addingNew && (
              <tr>
                <td colSpan={6} className="px-3 py-6 text-xs text-muted-foreground text-center">
                  No types yet. Click "Add Type" to create the first one.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Delete confirmation */}
      <AlertDialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Deactivate Type</AlertDialogTitle>
            <AlertDialogDescription>
              This will soft-delete "{deleteTarget?.display_name ?? deleteTarget?.code}". Existing
              requirements using this type are not affected. You can reactivate it later via the API.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                del.mutate(deleteTarget!.id);
                setDeleteTarget(null);
              }}
              disabled={del.isPending}
            >
              Deactivate
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ============================================================================
// Formula Tab — Rules + CSV import/export
// ============================================================================

function conditionJsonDisplay(cj: Record<string, string[]> | null): string {
  if (!cj) return '—';
  const parts: string[] = [];
  if (cj.all_of?.length) parts.push(`all_of: [${cj.all_of.join(', ')}]`);
  if (cj.any_of?.length) parts.push(`any_of: [${cj.any_of.join(', ')}]`);
  return parts.join(' | ') || '—';
}

interface RuleRowProps {
  rule: DocumentRequirementRule;
  onEdit: () => void;
  onDelete: () => void;
}

function RuleRow({ rule, onEdit, onDelete }: RuleRowProps) {
  return (
    <tr className="border-t hover:bg-muted/20 group text-xs">
      <td className="px-3 py-2 font-mono text-muted-foreground">
        {rule.requirement_type_name ?? rule.requirement_type_code ?? rule.requirement_type_id.slice(0, 8)}
      </td>
      <td className="px-3 py-2 text-muted-foreground">
        {rule.project_type_name ?? rule.project_type_id.slice(0, 8)}
      </td>
      <td className="px-3 py-2 font-mono text-muted-foreground">{rule.trigger_workflow_stage}</td>
      <td className="px-3 py-2 text-center">{rule.due_offset_days}</td>
      <td className="px-3 py-2 text-muted-foreground">{rule.recipient_role}</td>
      <td className="px-3 py-2 text-muted-foreground truncate max-w-[160px]">
        {conditionJsonDisplay(rule.condition_json)}
      </td>
      <td className="px-3 py-2 text-center">{rule.priority}</td>
      <td className="px-3 py-2">
        <span
          className={cn(
            'inline-flex rounded-full px-1.5 py-0.5 text-[10px] font-medium border',
            rule.active
              ? 'bg-green-50 text-green-700 border-green-200'
              : 'bg-gray-50 text-gray-500 border-gray-200',
          )}
        >
          {rule.active ? 'On' : 'Off'}
        </span>
      </td>
      <td className="px-3 py-2">
        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100">
          <button
            className="p-1 hover:bg-muted rounded text-muted-foreground"
            title="Edit"
            onClick={onEdit}
          >
            <Pencil className="w-3 h-3" />
          </button>
          <button
            className="p-1 hover:bg-destructive/10 hover:text-destructive rounded text-muted-foreground"
            title="Deactivate"
            onClick={onDelete}
          >
            <Trash2 className="w-3 h-3" />
          </button>
        </div>
      </td>
    </tr>
  );
}

interface ImportModalProps {
  open: boolean;
  onClose: () => void;
}

function ImportModal({ open, onClose }: ImportModalProps) {
  const importRules = useImportRequirementRules();
  const fileRef = useRef<HTMLInputElement>(null);
  const [csvText, setCsvText] = useState('');
  const [createMissing, setCreateMissing] = useState(false);
  const [results, setResults] = useState<any[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => setCsvText((ev.target?.result as string) ?? '');
    reader.readAsText(file);
  };

  const handleImport = async () => {
    if (!csvText.trim()) return;
    setError(null);
    setResults(null);
    try {
      const res: any = await importRules.mutateAsync({ csvText, createMissingTypes: createMissing });
      setResults(res?.rows ?? []);
    } catch (err: any) {
      setError(err.message || 'Import failed');
    }
  };

  const handleClose = () => {
    setCsvText('');
    setResults(null);
    setError(null);
    setCreateMissing(false);
    if (fileRef.current) fileRef.current.value = '';
    onClose();
  };

  if (!open) return null;

  return (
    <AlertDialog open={open} onOpenChange={(v) => !v && handleClose()}>
      <AlertDialogContent className="max-w-xl">
        <AlertDialogHeader>
          <AlertDialogTitle>Import Rules from CSV</AlertDialogTitle>
          <AlertDialogDescription>
            CSV columns: <code className="text-xs bg-muted px-1 rounded">project_type_code, trigger_workflow_stage, requirement_code, recipient_role, due_offset_days, condition_json</code>
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div className="space-y-3 py-2">
          <div className="flex gap-2 items-center">
            <Button
              variant="outline"
              size="sm"
              className="h-8"
              onClick={() => fileRef.current?.click()}
            >
              <Upload className="w-3.5 h-3.5 mr-1" />
              Choose CSV file
            </Button>
            <input ref={fileRef} type="file" accept=".csv,text/csv" className="hidden" onChange={handleFile} />
            {csvText && (
              <span className="text-xs text-muted-foreground">
                {csvText.split('\n').length - 1} data row(s) loaded
              </span>
            )}
          </div>

          <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
            <input
              type="checkbox"
              checked={createMissing}
              onChange={(e) => setCreateMissing(e.target.checked)}
              className="rounded"
              data-testid="create-missing-types-checkbox"
            />
            <span>Create missing requirement types on import</span>
          </label>
          <p className="text-xs text-muted-foreground">
            Default: strict mode — unknown requirement codes fail the row. Check above to auto-create
            missing types (useful for initial bulk import).
          </p>

          {/* Per-row results */}
          {results && (
            <div className="max-h-48 overflow-y-auto border rounded-lg divide-y text-xs">
              {results.map((row: any, i: number) => (
                <div
                  key={i}
                  className={cn(
                    'px-3 py-1.5 flex items-start gap-2',
                    row.success ? 'text-green-700' : 'text-red-700',
                  )}
                >
                  <span className="font-mono shrink-0">Row {row.row ?? i + 1}</span>
                  <span>{row.success ? 'OK' : row.error ?? 'Failed'}</span>
                </div>
              ))}
            </div>
          )}

          {error && (
            <p className="text-xs text-destructive">{error}</p>
          )}
        </div>

        <AlertDialogFooter>
          <AlertDialogCancel onClick={handleClose}>
            {results ? 'Close' : 'Cancel'}
          </AlertDialogCancel>
          {!results && (
            <AlertDialogAction
              onClick={handleImport}
              disabled={!csvText.trim() || importRules.isPending}
            >
              {importRules.isPending ? 'Importing…' : 'Import'}
            </AlertDialogAction>
          )}
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

const CSV_HEADERS =
  'project_type_code,trigger_workflow_stage,requirement_code,recipient_role,due_offset_days,condition_json\n';

function exportRulesToCsv(rules: DocumentRequirementRule[]): void {
  const rows = rules.map((r) => {
    const cj = r.condition_json ? JSON.stringify(r.condition_json) : '';
    const ptCode = r.project_type_name ?? r.project_type_id;
    const reqCode = r.requirement_type_code ?? r.requirement_type_id;
    return [ptCode, r.trigger_workflow_stage, reqCode, r.recipient_role, r.due_offset_days, cj]
      .map((v) => `"${String(v).replace(/"/g, '""')}"`)
      .join(',');
  });
  const csv = CSV_HEADERS + rows.join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'document-requirement-rules.csv';
  a.click();
  URL.revokeObjectURL(url);
}

function FormulaTab() {
  const { data: rules = [], isLoading } = useDocumentRequirementRules();
  const del = useDeleteRequirementRule();

  const [importOpen, setImportOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<DocumentRequirementRule | null>(null);

  if (isLoading) {
    return <p className="text-sm text-muted-foreground">Loading…</p>;
  }

  const activeRules = rules.filter((r) => r.active);
  const inactiveRules = rules.filter((r) => !r.active);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold">Formula Rules</h3>
          <p className="text-xs text-muted-foreground mt-0.5">
            Rules that auto-generate requirements when a project reaches a workflow stage.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {rules.length > 0 && (
            <Button
              variant="outline"
              size="sm"
              className="h-8"
              onClick={() => exportRulesToCsv(rules)}
            >
              <Download className="w-3.5 h-3.5 mr-1" />
              Export CSV
            </Button>
          )}
          <Button
            variant="outline"
            size="sm"
            className="h-8"
            onClick={() => setImportOpen(true)}
          >
            <Upload className="w-3.5 h-3.5 mr-1" />
            Import CSV
          </Button>
        </div>
      </div>

      <div className="border rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-muted/30 text-xs text-muted-foreground">
              <th className="px-3 py-2 text-left">Requirement</th>
              <th className="px-3 py-2 text-left">Project Type</th>
              <th className="px-3 py-2 text-left">Trigger Stage</th>
              <th className="px-3 py-2 text-center w-16">Offset (days)</th>
              <th className="px-3 py-2 text-left w-32">Recipient Role</th>
              <th className="px-3 py-2 text-left">Condition</th>
              <th className="px-3 py-2 text-center w-16">Priority</th>
              <th className="px-3 py-2 w-12" />
              <th className="px-3 py-2 w-16" />
            </tr>
          </thead>
          <tbody>
            {activeRules.map((rule) => (
              <RuleRow
                key={rule.id}
                rule={rule}
                onEdit={() => {/* inline editing via CSV import workflow */}}
                onDelete={() => setDeleteTarget(rule)}
              />
            ))}
            {inactiveRules.length > 0 && activeRules.length > 0 && (
              <tr>
                <td
                  colSpan={9}
                  className="px-3 py-1 text-[10px] text-muted-foreground bg-muted/10"
                >
                  Inactive rules ({inactiveRules.length}) — deactivated, not applied to new projects
                </td>
              </tr>
            )}
            {inactiveRules.map((rule) => (
              <RuleRow
                key={rule.id}
                rule={rule}
                onEdit={() => {}}
                onDelete={() => setDeleteTarget(rule)}
              />
            ))}
            {rules.length === 0 && (
              <tr>
                <td
                  colSpan={9}
                  className="px-3 py-6 text-xs text-muted-foreground text-center"
                >
                  No rules yet. Import a CSV or ask Jamie to define the formula.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <ImportModal open={importOpen} onClose={() => setImportOpen(false)} />

      <AlertDialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Deactivate Rule</AlertDialogTitle>
            <AlertDialogDescription>
              This rule will be deactivated. Existing requirements generated by this rule are not
              affected. Future generate calls will skip this rule.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                del.mutate(deleteTarget!.id);
                setDeleteTarget(null);
              }}
              disabled={del.isPending}
            >
              Deactivate
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ============================================================================
// Main: DocumentRequirementsTab
// ============================================================================

export function DocumentRequirementsTab() {
  const [subTab, setSubTab] = useState('types');

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex items-center gap-3">
        <div className="p-2 bg-blue-100 rounded-lg">
          <ClipboardList className="w-5 h-5 text-blue-600" />
        </div>
        <div>
          <h2 className="text-base font-semibold text-gray-900">Document Requirements</h2>
          <p className="text-xs text-gray-500 mt-0.5">
            Define required documents per project type and workflow stage.
          </p>
        </div>
      </div>

      {/* Sub-tab navigation */}
      <ViewTabs
        views={SUB_TABS}
        currentView={subTab}
        onChange={setSubTab}
      />

      {/* Sub-tab content */}
      {subTab === 'types' && <TypesTab />}
      {subTab === 'formula' && <FormulaTab />}
    </div>
  );
}
